import type { ChangeItem, Release } from "./types";

/**
 * Zettl's version history, newest first: the one source for the public page
 * (/:lang/changelog, public items only) and docs/CHANGELOG.md (everything).
 *
 * A pull request does not edit this file: it adds its own entry as
 * src/lib/changelog/unreleased/<pr>.json (CONTRIBUTING.md → "Changelog"), so
 * open pull requests never conflict here. `npm run changelog:release` turns
 * those entries into the next block below. After editing by hand, run
 * `npm run changelog`; keep package.json's version at RELEASES[0].version.
 *
 * Public text is plain language for customers, one sentence. Security items
 * say what the customer gets, never how the old behaviour could be abused;
 * that detail goes in `internal`. This file must stay free of runtime
 * imports (scripts/generate-changelog.mjs loads it with plain Node).
 */
export const RELEASES: readonly Release[] = [
  {
    version: "2.5.0",
    date: "2026-10-24",
    items: [
      {
        type: "improved",
        cs: "Edge funkce přijímají požadavky z náhledů pull requestů (pr-<N>.zettlapp.com, pr-<N>.zettl.cz).",
        en: "Edge functions accept pull request preview origins (pr-<N>.zettlapp.com, pr-<N>.zettl.cz).",
        public: false,
        internal:
          "Edge functions accept pull request preview origins (pr-<N>.zettlapp.com, pr-<N>.zettl.cz).",
        prs: [94],
      },
      {
        type: "security",
        cs: "Administrace vyžaduje druhý krok přihlášení kódem z ověřovací aplikace.",
        en: "The admin console requires a second sign-in step with an authenticator-app code.",
        public: false,
        internal:
          "verifyAdmin() (_shared/admin-auth.ts, and inline in admin-system-health) now also requires an aal2 session (Supabase MFA, TOTP) and answers 403 code MFA_REQUIRED otherwise; before, one password or a reset via the admin mailbox was all that guarded cross-tenant service-role access. AdminRoute sends the admin to TOTP setup (QR code + key) or the code prompt, both Supabase Auth only, so enrolling works before the functions are redeployed. Recovery: docs/OPERATIONS.md → Admin 2FA.",
        prs: [92],
      },
      {
        type: "new",
        cs: "Tarify Start a Firma teď platí podle ceníku: počet firem, účtů u Fio, uživatelů a dokladů přečtených AI za měsíc.",
        en: "The Start and Firma plans now apply as priced: the number of companies, Fio accounts, users and AI-read documents a month.",
        public: true,
        internal:
          "Plan enforcement switched on 24 Oct 16:10 UTC (billing_config.enforce = true) after migration 20261023130000_plan_enforcement.sql and the deploy of 14 functions. Guard triggers, billing_check(), atomic AI reservation, daily AI ceiling (Start 50, Firma 150). The 11 grandfathered companies keep free Firma.",
        prs: [70],
      },
      {
        type: "improved",
        cs: "AI přečte doklad do 10 MB a 20 stran. Větší soubor aplikace odmítne ještě před nahráním, ať ho rozdělíte.",
        en: "The AI reads documents up to 10 MB and 20 pages. The app refuses a larger file before uploading it, so you can split it.",
        public: true,
        internal:
          "_shared/document-limits.ts: size cap plus PDF page count (/Count of the page tree, else distinct /Page objects), checked before any AI call in extract-invoice, reprocess-invoice and the inbox; the browser runs the same check. extract-invoice no longer accepts image_url (cap bypass and SSRF).",
        prs: [70],
      },
      {
        type: "new",
        cs: "Nová stránka Kontakt s formulářem a přehledem, kam s čím napsat.",
        en: "A new Contact page with a form and an overview of where to write about what.",
        public: true,
        internal:
          "contact-form rewritten to match the production contact_messages table, with Reply-To, Turnstile and a per-sender throttle; support address podpora@zettl.cz. FAQ contour field pinned so opening answers no longer moves the background.",
        prs: [80],
      },
      {
        type: "new",
        cs: "Stránka Novinky s historií verzí.",
        en: "A What's new page with the version history.",
        public: true,
        internal: "src/lib/changelog/releases.ts as the one source; docs/CHANGELOG.md generated.",
        prs: [81],
      },
      {
        type: "improved",
        cs: "Příprava přechodu na zettl.cz a app.zettl.cz.",
        en: "Groundwork for the move to zettl.cz and app.zettl.cz.",
        public: false,
        internal:
          "Phase A: host-based routing in vercel.json, one URL table (src/lib/site/publicPages.ts), APP_URL via _shared/app-origin.ts. zettlapp.com unchanged; invitation links no longer default to the then-unlive zettl.cz.",
        prs: [82],
      },
      {
        type: "security",
        cs: "Odstraněny staré nepoužívané serverové funkce.",
        en: "Removed old, unused server functions.",
        public: false,
        internal:
          "Removed execute-sql (a stub that always returned an error) and test-fio-endpoints (called Fio with any token sent to it; no caller) from the repo, plus the stale N8N_WEBHOOK_SECRET mentions. 13 more were deployed with no source in the repo and no caller (Revolut, Raiffeisen, Erste, KB, invoices-update, matches-update, transactions-update, invite-team-member, debug-ai-gateway); they are deleted in Supabase per docs/OPERATIONS.md.",
        prs: [89],
      },
      {
        type: "security",
        cs: "Správcovská práva si už nikdo nemůže přidělit sám.",
        en: "Nobody can grant themselves admin rights any more.",
        public: false,
        internal:
          "Migration 20261024210000_protect_is_superadmin.sql: a trigger refuses changes to profiles.is_superadmin unless they come from the service role or a database owner. Users could set the legacy flag on their own row (RLS allows own-row updates); it only opened the empty sandbox schema and no code reads it, but it was a latent escalation. CLAUDE.md now documents the real admin check (PLATFORM_SUPERADMIN_EMAIL).",
        prs: [91],
      },
      {
        type: "improved",
        cs: "Odstraněna nepoužívaná synchronizace s Google Drive.",
        en: "Removed the unused Google Drive sync.",
        public: false,
        internal:
          "Google Drive removed (0 connections, never marketed): its 6 functions plus google-config, the daily-sync step and the monitor-critical-flows entry; the two tables stay, empty, until a drop migration. Before that, google-auth-callback got a return URL allow-list and escaped pages (#83) and a server-signed OAuth state (#84).",
        prs: [83, 84, 87],
      },
      {
        type: "new",
        cs: "Zettl se přestěhoval: web je na zettl.cz a aplikace na app.zettl.cz. Staré adresy na zettlapp.com vás přesměrují.",
        en: "Zettl has moved: the website is at zettl.cz and the app at app.zettl.cz. Old zettlapp.com addresses forward you there.",
        public: true,
        internal:
          "Phase B, live 24 Oct 20:54 UTC: SITE_LAYOUT root, PRODUCTION_ORIGIN https://zettl.cz; zettlapp.com and www 308 to zettl.cz (app routes to app.zettl.cz) in one hop, except /mcp, /.well-known, /oauth, ?next=/oauth and ?code= which stay on zettlapp.com for the MCP connector. Only zettl.cz is indexable. The @zettlapp.com receipt inboxes are unchanged.",
        prs: [90],
      },
      {
        type: "new",
        cs: "Nová administrace: přehled firem, předplatného, synchronizace a provozu na jednom místě, detail firmy a uživatele, schránka zpráv.",
        en: "A new admin console: companies, subscriptions, sync and operations at a glance, company and user detail, and a message inbox.",
        public: false,
        internal:
          "Admin only (/:lang/admin). New edge functions admin-overview, admin-companies, admin-inbox (service role after verifyAdmin; every write audit-logged: extend trial, resend invitation, mark message read). The invitation e-mail moved to _shared/invitation-email.ts. Removed the legacy one-off functions run-migration and check-migration (callable by any signed-in user, ran DDL with the service role); delete them in Supabase with `npx supabase functions delete <fn>`.",
        prs: [85],
      },
      {
        type: "new",
        cs: "Administrace má vývojářskou konzoli: nasazené funkce, stav migrací, nastavené klíče a vypínač limitů tarifů.",
        en: "The admin console gets a dev console: deployed functions, migration status, configured secrets and the plan-limit switch.",
        public: false,
        internal:
          "Admin only (/:lang/admin/dev). Edge function admin-dev: OPTIONS probe of each repo function (404 = not deployed; functions without a preflight handler are not probed), presence booleans for a fixed secret allow-list (SECRET_ALLOWLIST), recent function errors, and the billing_config.enforce toggle (typed word, reason, second confirmation, audit-logged; shows who changed it last). Build-time repo manifest via virtual:repo-manifest. Migration 20261024200000_admin_console.sql: admin_applied_migrations() and admin_cron_status(), service_role only.",
        prs: [86],
      },
    ],
  },
  {
    version: "2.4.1",
    date: "2026-10-24",
    items: [
      {
        type: "improved",
        cs: "Bezplatný přístup teď platí pro majitele: když založíte nebo převezmete další firmu, dostane stejný tarif zdarma, nejvýše pro 3 firmy.",
        en: "Free access now follows its owner: a company you create or take over gets the same free plan, for up to 3 companies.",
        public: true,
        internal:
          "Migration 20261024140000_free_access_per_owner.sql (one function, one trigger): the free override's plan and end date are copied to further companies of the same owner, capped at Firma's company limit of 3.",
        prs: [78],
      },
      {
        type: "improved",
        cs: "Na úvodní stránce tvoří závěrečná výzva a patička jeden celek a vrstevnice za častými dotazy jsou lépe vidět.",
        en: "Landing page: the final call to action and the footer read as one closing block, and the contours behind the FAQ are visible.",
        public: false,
        internal: "Landing visuals only (CLOSING_BAND, ContourField variants).",
        prs: [77],
      },
    ],
  },
  {
    version: "2.4.0",
    date: "2026-10-23",
    summary: {
      cs: "Předplatné, nový web, nový Rozpočet a Analytika a řada oprav.",
      en: "Subscriptions, a new website, a new Budget and Analytics, and a long list of fixes.",
    },
    items: [
      {
        type: "new",
        cs: "Předplatné: tarif Start za 150 Kč měsíčně a Firma za 350 Kč měsíčně, placené kartou a spravované v Nastavení → Předplatné.",
        en: "Subscriptions: the Start plan at 150 CZK a month and Firma at 350 CZK a month, paid by card and managed in Settings → Subscription.",
        public: true,
        internal:
          "Stripe Checkout and Customer Portal. Migration 20261023120000_billing.sql (billing tables, org_plan(), billing_start_trial(), webhook writers); functions billing-create-checkout, billing-portal, stripe-webhook (verify_jwt off, signature-verified, idempotent events); delete-user-account cancels subscriptions first. Yearly prices were dropped before launch (#68, #69): monthly billing only. Went live with plans draft: false in #75. Plan enforcement (billing_config.enforce, #70) is not switched on yet.",
        prs: [61, 69, 75],
      },
      {
        type: "new",
        cs: "Nové firmy mají prvních 14 dní zdarma, bez platební karty.",
        en: "New companies get the first 14 days free, no card required.",
        public: true,
        internal:
          "The owner's trial starts on first use of the app via billing_start_trial() (useBilling); skipped for companies with free access.",
        prs: [61, 75],
      },
      {
        type: "new",
        cs: "Stávající zákazníci mají tarif Firma zdarma.",
        en: "Existing customers get the Firma plan free of charge.",
        public: true,
        internal:
          "billing_grandfather_existing_orgs(): all 11 existing companies got a grandfathered Firma override with no end date. Admin page /admin/free-access (function billing-admin-free-access) grants Start/Firma for free with an optional end date; the admin note column is hidden from members by a column grant.",
        prs: [69, 75],
      },
      {
        type: "new",
        cs: "Nový Rozpočet: každou pravidelnou platbu ověří proti výpisu z Fio a sám navrhne pravidelné platby, které v rozpočtu ještě nemáte.",
        en: "A new Budget: every recurring payment is checked against your Fio statement, and recurring payments the budget doesn't track yet are suggested.",
        public: true,
        internal:
          "Statuses Zaplaceno / Jiná částka / Nedorazilo / Čeká / Bez dat; month matrix; pause and resume. Migration 20261022120000_business_budget_category_key.sql (business_category, paused_from, resumed_on). Logic in src/lib/budget/.",
        prs: [54],
      },
      {
        type: "improved",
        cs: "Analytika je nově přehled pro majitele: srovnání s loňským rokem, cash flow, kdo platí, neuhrazené pohledávky, pokrytí doklady a DPH na výstupu.",
        en: "Analytics is now an owner's report: comparison with last year, cash flow, who pays, unpaid receivables, document coverage and output VAT.",
        public: true,
        internal:
          "Frontend only, logic in src/lib/analytics/. Period tabs Month / Quarter / Year / Custom; aging buckets 1–30 / 31–60 / over 60 days; client concentration warning above 40 %. Exchange rates are static; VAT is output VAT only.",
        prs: [55],
      },
      {
        type: "improved",
        cs: "Nový web zettlapp.com ukazuje, jak párování plateb s doklady funguje, a uvádí ceny.",
        en: "A new zettlapp.com shows how matching payments with documents works, and lists the prices.",
        public: true,
        internal:
          'Landing rebuilt around Fio payment matching (#56), then product mockups and checkmark pricing (#66), hero and mockup polish (#67), contours, theme-following bands, "Získat" buttons, monthly-only pricing (#68), Pro koho dropped (#71), contours behind every second section (#73).',
        prs: [56, 66, 67, 68, 71, 73],
      },
      {
        type: "improved",
        cs: "Veřejné stránky přicházejí jako hotové HTML, takže se zobrazí hned i bez JavaScriptu.",
        en: "Public pages arrive as ready-made HTML, so they show right away, even without JavaScript.",
        public: true,
        internal:
          "Prerendered public pages (src/entry-server.tsx, scripts/prerender.mjs), robots.txt, sitemap, JSON-LD, llms.txt / llms-full.txt, OG images, lazy app chunks. Lighthouse mobile 47 → 89–90, LCP 8.6 s → about 2 s. #58 describes the MCP connector precisely for AI readers.",
        prs: [53, 58],
      },
      {
        type: "improved",
        cs: "Nové obchodní podmínky (verze 2.0), zásady ochrany osobních údajů a zpracovatelská smlouva; pro stávající zákazníky platí nové podmínky od 15.\u00a0listopadu 2026.",
        en: "New terms of service (version 2.0), privacy policy and data processing agreement; for existing customers the new terms apply from 15 November 2026.",
        public: true,
        internal:
          "Published 23 Oct 2026. Privacy, DPA and cookie policy corrected (claims about TOTP MFA, account lockout and no transfers outside the EU removed; processors added). Required terms checkbox on the access request: migration 20261023080000_waiting_list_terms_acceptance.sql, join-waiting-list requires terms_version.",
        prs: [63],
      },
      {
        type: "security",
        cs: "Když zrušíte účet, smažeme spolu s ním i nahrané doklady a loga.",
        en: "Deleting your account now also deletes your uploaded documents and logos.",
        public: true,
        internal:
          "delete-user-account, google-drive-sync-folder, google-drive-webhook and _shared: a stored file is removed only when no surviving row in any tenant references it; origin checks and keyset paging, so one tenant can never remove another's file. Deployed per #75.",
        prs: [64],
      },
      {
        type: "improved",
        cs: "Zettl je česky a anglicky; odkazy na německou verzi vedou na anglickou.",
        en: "Zettl is in Czech and English; links to the German version lead to English.",
        public: true,
        internal:
          "German UI dropped; /de/* → /en/* with 308; zettl_lang cookie for the / redirect. Invoice and PDF document languages untouched (German invoices can still be issued).",
        prs: [60],
      },
      {
        type: "fixed",
        cs: "Přehledy už nevynechávají data u firem s více než 1000 záznamy.",
        en: "Views no longer leave out data for companies with more than 1,000 records.",
        public: true,
        internal:
          "fetchAllRows / fetchInChunks past PostgREST's 1000-row cap; 154 query sites audited. Still capped (not fixed here): daily-sync, run-ai-matching, export-user-data, mcp financial_summary, monitor-critical-flows.",
        prs: [52],
      },
      {
        type: "fixed",
        cs: "Zrušení spárování odebere jen vybraný doklad a transakce se po něm už neukazuje jako spárovaná.",
        en: "Unmatching removes only the chosen document, and the transaction no longer shows as matched afterwards.",
        public: true,
        internal:
          "Dashboard unmatch removed every document on the transaction; receipts unmatch left the transaction matched with no document.",
        prs: [52],
      },
      {
        type: "fixed",
        cs: "Párování podle variabilního symbolu už nepřiřadí špatnou fakturu.",
        en: "Matching by variable symbol no longer links the wrong invoice.",
        public: true,
        prs: [52],
      },
      {
        type: "fixed",
        cs: "Nahraný výpis GPC už neduplikuje platby, které Zettl stáhl z Fio přes API.",
        en: "An uploaded GPC statement no longer duplicates payments Zettl already fetched from Fio's API.",
        public: true,
        internal: "Ledger duplicates from GPC re-inserting API-synced rows.",
        prs: [52],
      },
      {
        type: "improved",
        cs: "Hledání pokrývá i protistranu a variabilní symbol a platby přes PayPal a SumUp mají správného příjemce.",
        en: "Search also covers the counterparty and variable symbol, and PayPal and SumUp payments show the right payee.",
        public: true,
        internal:
          "Also: dashboard deep links ?q= and ?tx=; the invoice category filter keeps NULL rows; invite and sign-in links keep the language and the invite. docs/billing/STRIPE_INTEGRATION.md added.",
        prs: [52],
      },
      {
        type: "fixed",
        cs: "Nahrávání dokladů přetažením a z výběru dokladu při párování znovu funguje.",
        en: "Uploading documents by drag and drop, and from the document picker while matching, works again.",
        public: true,
        internal:
          "Both failed with 400 in production: the upload did not send organization_id. Frontend only; membership is still checked server-side.",
        prs: [72],
      },
      {
        type: "fixed",
        cs: "Okno otevřené během aktualizace Zettlu se jednou obnoví místo chybové hlášky.",
        en: "A tab left open during a Zettl update reloads once instead of showing an error.",
        public: true,
        internal:
          "lazyWithReload (at most one reload per 30 s); vercel.json rewrite no longer serves index.html for /assets/.",
        prs: [62],
      },
      {
        type: "improved",
        cs: "Po odhlášení vás stránky aplikace pošlou na přihlášení a po něm zpět, kde jste byli.",
        en: "When you're signed out, app pages send you to sign in and then back where you were.",
        public: true,
        internal:
          "One RequireSession guard in AppLayout with ?next=. No data was exposed before: RLS returned nothing, the pages just rendered an empty shell.",
        prs: [65],
      },
    ],
  },
  {
    version: "2.3.0",
    date: "2026-10-22",
    items: [
      {
        type: "improved",
        cs: "Nový vzhled celé aplikace: nový boční panel s přepínačem firmy, nový přehled s měsíčními dlaždicemi a stránky dříve zčásti v angličtině jsou přeložené.",
        en: "A new look across the app: a new sidebar with a company switcher, a new dashboard with month tiles, and pages that were partly in English are translated.",
        public: true,
        internal:
          "Flat sidebar with its own icons, collapsible to an icon rail, unmatched-count badge; workspace switcher (company + Business/Private) replaces the top bar; dashboard period header and status strip filtering the month table; shared PageHeader and year switch; better light-mode contrast. Frontend only.",
        prs: [50],
      },
      {
        type: "fixed",
        cs: "Enter v poli IČO už neodešle formulář klienta, osobní Analytika nepočítá příjmy jako výdaje a částky v rozpočtu mají český formát.",
        en: "Enter in the company ID field no longer submits the client form, personal Analytics no longer counts income as spending, and budget amounts use Czech formatting.",
        public: true,
        prs: [50],
      },
      {
        type: "improved",
        cs: "Design systém, testovací prostředí pro obrazovky a sdílené určení protistrany.",
        en: "Design system, UI harness and a shared counterparty helper.",
        public: false,
        internal:
          "docs/design/DESIGN_SYSTEM.md, scripts/ui-harness/, src/lib/finance/counterparty.ts.",
        prs: [51],
      },
    ],
  },
  {
    version: "2.2.0",
    date: "2026-10-20",
    items: [
      {
        type: "new",
        cs: "Propojte Zettl s AI asistentem, například Claude: přihlásíte se účtem Zettl a asistent vidí vaše faktury, doklady a platby.",
        en: "Connect Zettl to an AI assistant such as Claude: sign in with your Zettl account and the assistant can see your invoices, documents and payments.",
        public: true,
        internal:
          "Remote MCP server at https://zettlapp.com/mcp. Functions mcp and mcp-oauth; migration 20261019100000_mcp_oauth.sql. OAuth 2.1 with dynamic client registration and PKCE; 1 h access tokens, rotating refresh tokens; only token hashes stored; single-use codes (a replayed code revokes the client's tokens). Calls run as the user via a short-lived JWT, so RLS applies. Read tools by default; match / unmatch / categorize / set invoice status need zettl:write. Connections listed and revocable in Settings → Integrations.",
        prs: [41],
      },
      {
        type: "security",
        cs: "Přihlášení a obnovu hesla chrání ochrana proti robotům (CAPTCHA).",
        en: "Sign-in and password reset are protected against bots with a CAPTCHA.",
        public: true,
        internal:
          "Cloudflare Turnstile on sign-in, forgot password, the current-password check in Settings → Account and the waitlist (join-waiting-list verifies captcha_token; _shared/turnstile). Supabase CAPTCHA enforcement switched on manually after merge (docs/OPERATIONS.md → CAPTCHA). #46 names Cloudflare (Turnstile) as a processor in the privacy policy.",
        prs: [45, 46],
      },
      {
        type: "security",
        cs: "Zálohy se šifrují před nahráním a každý týden zkoušíme jejich obnovu.",
        en: "Backups are encrypted before upload and restored in a weekly drill.",
        public: false,
        internal:
          "db-backup.yml encrypts with gpg (AES256) and fails closed without a key; nightly encrypted storage snapshot replaces the file mirror; backup-restore-drill.yml runs Sundays 04:00 UTC; 30-day retention. Backups were previously uploaded unencrypted; the old plain archives are still in the bucket (owner's call).",
        prs: [48],
      },
      {
        type: "improved",
        cs: "Každý náhled pull requestu má adresu pr-<N>.zettlapp.com, která po zavření zmizí.",
        en: "Every pull request preview gets a pr-<N>.zettlapp.com address, removed when the PR closes.",
        public: false,
        internal: "preview-domain.yml (#45) and its cleanup on close (#47).",
        prs: [45, 47],
      },
    ],
  },
  {
    version: "2.1.2",
    date: "2026-09-10",
    items: [
      {
        type: "fixed",
        cs: "Monitoring dostupnosti přestal hlásit výpadky, které nenastaly.",
        en: "The uptime monitor stopped reporting outages that never happened.",
        public: false,
        internal:
          "public-health answers 200 with the incident state in the body (503 only when the monitoring store is unreachable) and its source is now in the repo; monitor-critical-flows treats a throwing check as a warning with its real message; detect-monitoring-incidents opens an incident only after two failing runs in a row. Not deployed at merge (deploy commands in the PR).",
        prs: [40],
      },
    ],
  },
  {
    version: "2.1.1",
    date: "2026-09-04",
    items: [
      {
        type: "fixed",
        cs: "Nahrání souboru se stejným názvem už nepřepíše starší doklad.",
        en: "Uploading a file with the same name no longer overwrites an older document.",
        public: true,
        internal:
          "extract-invoice always inserts. 4 receipts had been overwritten in production; their created_at was repaired.",
        prs: [38],
      },
      {
        type: "fixed",
        cs: "U platby spárované s více doklady ukazuje i druhý doklad svou transakci.",
        en: "When a payment is matched to several documents, the second one shows its transaction too.",
        public: true,
        internal: "src/lib/receipts/receiptTransactionLink.ts.",
        prs: [38],
      },
      {
        type: "fixed",
        cs: "Noční synchronizace se vejde do časového limitu a monitoring neposílá opakovaná upozornění.",
        en: "The nightly sync stays within its time limit, and monitoring stops sending repeated alerts.",
        public: false,
        internal:
          "daily-sync runWithinBudget (no new work after 110 s of 150 s; runs left `running` over 15 min are reaped); detect-monitoring-incidents fingerprint normalisation (one stuck run had sent 51 e-mails in 12 h). #39: acknowledging an incident sticks, warnings re-notify daily (critical hourly), escalation breaks through snooze; logic in notify.ts.",
        prs: [38, 39],
      },
    ],
  },
  {
    version: "2.1.0",
    date: "2026-08-27",
    items: [
      {
        type: "new",
        cs: "Import bankovních výpisů ve formátu GPC/ABO: několik souborů najednou, s náhledem každého výpisu a kontrolou zůstatku.",
        en: "Import bank statements in GPC/ABO format: several files at once, with a preview of each statement and a balance check.",
        public: true,
        internal:
          "Client-side in src/lib/gpc/, from a button in the Transactions header. Row ids are SHA-256 of the source record; reconcileAgainstLedger skips what the Fio API already synced. Migrations 20260825120000_add_transaction_counterparty_columns.sql (variable_symbol, counter_account, counter_bank_code, counter_name) and 20260825130000_fio_only_drop_dead_bank_columns.sql (Fio only).",
        prs: [34],
      },
      {
        type: "improved",
        cs: "Párování pracuje s doklady a platbami celé firmy, ať je nahrál kterýkoli člen, a platby s přesně stejnou částkou spáruje hned.",
        en: "Matching works across the whole company, whoever uploaded the documents and payments, and pairs exact amounts right away.",
        public: true,
        internal:
          "run-ai-matching scoped by organization instead of user; deterministic exact-amount pre-pass before the AI; the AI also gets variable_symbol and counter_name.",
        prs: [35],
      },
      {
        type: "fixed",
        cs: "Odmítnutí navrženého spárování se teď uloží a import ISDOC zachová DPH.",
        en: "Rejecting a suggested match is now saved, and ISDOC import keeps the VAT.",
        public: true,
        internal: "Also: an invoice's variable symbol is no longer set to the raw invoice number.",
        prs: [34],
      },
      {
        type: "fixed",
        cs: "Výpis, který už naimportovala jiná firma, se naimportuje správně a nehlásí samé duplicity.",
        en: "A statement another company has already imported now imports correctly instead of reporting only duplicates.",
        public: true,
        internal:
          "GPC row ids now include organizationId in the seed: identical ids across organizations made one company's rows block another's import (\"Imported 0, skipped 105 duplicates\").",
        prs: [36],
      },
      {
        type: "security",
        cs: "Neúspěšná přihlášení se znovu zapisují do bezpečnostního záznamu.",
        en: "Failed sign-ins are recorded in the security log again.",
        public: false,
        internal:
          "The code called an RPC log_security_event that did not exist, so failed-auth events were never recorded.",
        prs: [34],
      },
      {
        type: "improved",
        cs: "Kontrola typů v CI skutečně kontroluje.",
        en: "The CI type-check actually checks.",
        public: false,
        internal: "npm run type-check had never checked anything; 93 errors fixed.",
        prs: [34],
      },
    ],
  },
];

/** The releases as the public page shows them: public items only, empty releases left out. */
export function publicReleases(): Release[] {
  return RELEASES.map((r) => ({
    ...r,
    items: r.items.filter((i: ChangeItem) => i.public),
  })).filter((r) => r.items.length > 0);
}
